import { Alert, Box, Stack, SxProps } from "@mui/material";
import { SSText } from "src/components/base/components/SSText";
import { useSSTheme } from "../../reusable/hooks/useSSTheme";
import {
  SSButton,
  TSSButtonProps,
} from "src/components/base/components/SSButton";
import { OverlayedImage } from "src/components/base/components/OverlayedImage";
import React from "react";
import { useViewScheduleModal } from "../../reusable/components/GlobalModals/ViewScheduleModal";
import { TPass } from "src/shared/types/globalStotre.types";
import { useGlobalStore } from "../../reusable/hooks/useGlobalStore";
import { calcClaimableMaxSeatCount, isUserHasThePass } from "src/utils/utils";
import placeHolderImage from "public/images/empty_state_image.png";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { getPassCardPrice } from "./utils.purchaseFlow";
import { usePassFlow } from "./usePassFlow";
import CanvasParticles from "src/components/reusable/components/CanvasParticles";
import { GoGift } from "react-icons/go";

export function ViewScheduleButton(props: { pass: TPass }) {
  const theme = useSSTheme();
  const viewScheduleModal = useViewScheduleModal();

  return (
    <SSButton
      size="small"
      variant="text"
      colorScheme={theme.colors.teamSecondary}
      disableFocusRipple
      sx={{
        textAlign: "left",
        textDecoration: "underline",
        padding: "0px",
        fontWeight: 500,
        textUnderlineOffset: "2px",
        // borderRadius: "4px",
        "&:hover": {
          textDecoration: "underline",
        },
      }}
      onClick={() => {
        viewScheduleModal.set({
          isOpen: true,
          subscriptionPlan: props.pass,
        });
      }}
    >
      View Schedule
    </SSButton>
  );
}

type FAQButtonProps = {
  onClick?: () => void;
};

function FAQButton({ onClick }: FAQButtonProps) {
  const theme = useSSTheme();
  const { LANDING_PAGE_SETTINGS } = useTeamConfig();

  return (
    <SSButton
      size="small"
      variant="text"
      colorScheme={theme.colors.teamSecondary}
      disableFocusRipple
      sx={{
        textAlign: "left",
        textDecoration: "underline",
        padding: "0px",
        fontWeight: 500,
        textUnderlineOffset: "2px",
        "&:hover": {
          textDecoration: "underline",
        },
        ...LANDING_PAGE_SETTINGS?.PASS?.FAQ_BUTTON_STYLE,
      }}
      onClick={onClick}
    >
      FAQs
    </SSButton>
  );
}

export const PassSubscribeNowButton = (props: {
  pass: TPass;
  sx?: SxProps;
  onClick?: () => void;
}) => {
  const theme = useSSTheme();
  const { LANDING_PAGE_SETTINGS } = useTeamConfig();
  const { user, passesOnAccount } = useGlobalStore();
  const { onClickSubscribe } = usePassFlow();
  const [isLoading, setIsLoading] = React.useState(false);
  const claimableMaxSeatCount = calcClaimableMaxSeatCount(props.pass);
  const isMaxSeatClaimed = !!user && claimableMaxSeatCount == 0;

  if (props?.pass?.isSoldOut) {
    return (
      <Stack alignItems={"center"}>
        <Alert
          severity="success"
          color="warning"
          sx={{
            px: "16px",
            py: "0px",
            alignItems: "center",
            width: "fit-content",
            "& .MuiAlert-icon": { display: "none" },
            "& .MuiAlert-message": { fontWeight: 600 },
          }}
        >
          Sold Out
        </Alert>
      </Stack>
    );
  }

  return (
    <SSButton
      loading={isLoading}
      disabled={isLoading || isMaxSeatClaimed}
      variant="contained"
      colorScheme={theme.colors.teamPrimary}
      fullWidth
      size="large"
      sx={props.sx}
      onClick={async (ev: any) => {
        setIsLoading(true);
        if (props?.onClick) await props.onClick();
        else await onClickSubscribe(props?.pass?.passId);
        if (props?.pass?.errorMessage) setIsLoading(false);
      }}
    >
      {isMaxSeatClaimed
        ? "Max Claimed"
        : isUserHasThePass(passesOnAccount, props?.pass?.passId)
          ? "Add More"
          : props?.pass?.isOptOutRestricted &&
              LANDING_PAGE_SETTINGS?.PASS
                ?.SUBSCRIBE_BUTTON_TEXT_OPT_OUT_RESTRICTED
            ? LANDING_PAGE_SETTINGS?.PASS
                ?.SUBSCRIBE_BUTTON_TEXT_OPT_OUT_RESTRICTED
            : LANDING_PAGE_SETTINGS?.PASS?.SUBSCRIBE_BUTTON_TEXT ||
              "Subscribe Now"}
    </SSButton>
  );
};

export const PassGiftButton = (
  props: Omit<TSSButtonProps, "children"> & {
    pass: TPass;
  }
) => {
  const theme = useSSTheme();
  const { user } = useGlobalStore();
  const { onClickSendAsGift } = usePassFlow();
  const [isLoading, setIsLoading] = React.useState(false);
  const claimableMaxSeatCount = calcClaimableMaxSeatCount(props.pass);
  const isMaxSeatClaimed = !!user && claimableMaxSeatCount == 0;

  if (!props?.pass?.isGiftEligible || isMaxSeatClaimed) return null;

  return (
    <SSButton
      loading={isLoading}
      disabled={isLoading || isMaxSeatClaimed}
      variant={props?.variant || "outlined"}
      colorScheme={props?.colorScheme || theme.colors.teamPrimary}
      fullWidth
      size="large"
      startIcon={<GoGift />}
      {...props?.buttonProps}
      sx={{
        overflow: "hidden",
        position: "relative",
        fontWeight: 600,
        lineHeight: 1.1,
        whiteSpace: "nowrap",
        ...props?.buttonProps?.sx,
      }}
      onClick={async (ev: any) => {
        setIsLoading(true);
        if (props?.onClick) await props.onClick();
        else await onClickSendAsGift(props?.pass?.passId);
        if (props?.pass?.errorMessage) setIsLoading(false);
      }}
    >
      <CanvasParticles
        zIndex={0}
        numParticles={30}
        shapes={["square", "triangle"]}
        sizeRange={[2, 4]}
        speedRange={[30, 60]}
        swayAmplitudeRange={[4, 8]}
        swayPeriodRange={[1.2, 2.2]}
        rotationSpeedRange={[30, 60]}
        opacityRange={[0.2, 0.4]}
      />
      <span style={{ position: "relative", zIndex: 1 }}>Send as Gift</span>
    </SSButton>
  );
};

export interface TPassCardProps {
  pass: TPass;
  variant?: "variant1" | "variant2";
  showViewScheduleButton?: boolean;
  hideImage?: boolean;
  onClickSubscribe?: () => void;
  onClickSendAsGift?: () => void;
  showFAQButton?: boolean;
  hideSubscribeButton?: boolean;
  giftButtonVariant?: "outlined" | "contained";
  onClickFAQButton?: (passId: number) => void;
}

const PassCardVariant1 = (props: TPassCardProps) => {
  const theme = useSSTheme();
  const { sm: smUp } = useCssMediaQuery("up");
  const teamConfig = useTeamConfig();

  return (
    <Stack
      alignItems={"center"}
      justifyContent={"flex-end"}
      width={"100%"}
      height={"100%"}
      border={`1px solid ${theme.colors.gray[100]}`}
      borderRadius={3}
      p={3}
    >
      <Stack
        spacing={3}
        maxWidth={smUp ? "600px" : "100%"}
        textAlign={"center"}
        width={"100%"}
        height={"100%"}
      >
        <Stack px={2} spacing={1}>
          <SSText
            variant="h4"
            color={theme.colors.teamPrimary}
            lineHeight={1.1}
            fontWeight={500}
            dangerouslySetInnerHTML={{ __html: props.pass.passTitle }}
          />
          {!teamConfig.LANDING_PAGE_SETTINGS?.PASS?.HIDE_PASS_PRICE ? (
            <SSText variant="h4" color={theme.colors.gray[400]}>
              ${getPassCardPrice(props.pass, teamConfig)}
            </SSText>
          ) : null}
          <SSText
            variant="body"
            color={theme.colors.gray[400]}
            dangerouslySetInnerHTML={{ __html: props.pass.passDescription }}
          />
        </Stack>

        <Box flexGrow={1} mt={"0 !important"} />

        <Stack width={"100%"} alignItems={"center"} spacing={1}>
          {!props?.hideSubscribeButton ? (
            <PassSubscribeNowButton
              pass={props.pass}
              onClick={props?.onClickSubscribe}
              sx={{
                backgroundColor:
                  teamConfig.LANDING_PAGE_SETTINGS?.PASS
                    ?.SUBSCRIBE_BUTTON_COLOR ??
                  teamConfig.TEAM_GLOBAL_SETTINGS?.COLORS_PRIMARY,
              }}
            />
          ) : null}

          <PassGiftButton
            pass={props.pass}
            onClick={props?.onClickSendAsGift}
            variant={props?.giftButtonVariant || "outlined"}
          />

          {props?.showViewScheduleButton && props?.pass?.events.length ? (
            <ViewScheduleButton pass={props.pass} />
          ) : null}

          {props?.showFAQButton ? (
            <FAQButton
              onClick={() => props?.onClickFAQButton?.(props?.pass?.passId)}
            />
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
};

const PassCardVariant2 = (props: TPassCardProps) => {
  const theme = useSSTheme();
  const teamConfig = useTeamConfig();

  return (
    <Stack
      direction={"column"}
      border={`1px solid ${theme.colors.border}`}
      borderRadius={2.5}
      alignItems={"flex-start"}
      overflow={"hidden"}
      height={"100%"}
    >
      {!props?.hideImage ? (
        <Box
          width={"100%"}
          height={"230px"}
          overflow={"hidden"}
          position={"relative"}
        >
          <OverlayedImage
            alt={`${props.pass.passTitle}`}
            src={props?.pass?.passBackgroundUrl_lg || placeHolderImage.src}
            width={2160}
            height={2700}
          />
        </Box>
      ) : null}

      <Stack p={2} spacing={2} flex={1} width={"100%"}>
        <Box textAlign={"left"}>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"flex-start"}
            spacing={2}
          >
            <SSText
              variant="h6"
              color={theme.colors.gray["400"]}
              fontWeight={700}
              dangerouslySetInnerHTML={{ __html: props.pass.passTitle }}
            />

            <SSText
              variant="body"
              color={theme.colors.gray["300"]}
              minWidth={"fit-content"}
            >
              ${getPassCardPrice(props.pass, teamConfig)}
            </SSText>
          </Stack>
          <SSText
            mt={1}
            variant="body"
            color={theme.colors.gray["200"]}
            fontSize={`0.9rem`}
            fontWeight={500}
            dangerouslySetInnerHTML={{ __html: props.pass.passDescription }}
          />
        </Box>
        <Box flexGrow={1} />
        <Stack spacing={1}>
          {!props?.hideSubscribeButton ? (
            <PassSubscribeNowButton
              pass={props.pass}
              onClick={props?.onClickSubscribe}
              sx={{
                backgroundColor:
                  teamConfig.LANDING_PAGE_SETTINGS?.PASS
                    ?.SUBSCRIBE_BUTTON_COLOR ??
                  teamConfig.TEAM_GLOBAL_SETTINGS?.COLORS_PRIMARY,
              }}
            />
          ) : null}

          <PassGiftButton
            pass={props.pass}
            onClick={props?.onClickSendAsGift}
            variant={props?.giftButtonVariant || "outlined"}
          />

          {props?.showViewScheduleButton && props?.pass?.events.length ? (
            <ViewScheduleButton pass={props.pass} />
          ) : null}

          {props?.showFAQButton ? (
            <FAQButton
              onClick={() => props?.onClickFAQButton?.(props?.pass?.passId)}
            />
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
};

export function PassCard(props: TPassCardProps) {
  if (props?.variant == "variant1") return <PassCardVariant1 {...props} />;
  if (props?.variant == "variant2") return <PassCardVariant2 {...props} />;
  if (!props?.variant) return <PassCardVariant2 {...props} />;
}
